/* V2 -- Video Call Room */

import * as React from "react";
import { connect } from "react-redux";
import * as _ from "lodash";
import { IAppointmentScheduleResponse } from "@curefit/care-common";
import VideoCallActionBar from "../../components/teleconsultation/VideoCallActionBar";
import { OrderActions } from "../../actions/OrderActions";
import { IOrderEvent } from "../../models/order/IOrderEvent";
import {
    JOIN_UNJOIN_CALL_EVENTS,
    TwilioRoomEventsLog,
    AppConstants,
    toastNotificationAppearance
} from "../../utils/AppConstants";
import ModelFactory from "../../models/ModelFactory";
import AppUtils from "../../utils/AppUtils";
import { OngoingConsultationActions } from "../../actions/OngoingConsultationActions";
import { IFetchConfigResponse } from "../../models/IFetchLoggingConfig";
import IDoctorProfile from "../../models/IDoctorProfile";
import {
    connect as twilioVideoConnect,
    Room,
    LocalParticipant,
    StatsReport,
    LocalTrackPublication,
    LocalAudioTrack,
    NetworkQualityStats,
    LocalAudioTrackPublication,
    RemoteParticipant,
    NetworkQualityLevel,
    isSupported
} from "twilio-video";
import { EpochConstants } from "../../utils/AppUtils";
import { MobileConstants } from "../../utils/MobileConstants";
import LocalStorageUtils from "../../utils/LocalStorageUtils";
import { EventTracker } from "aws-sdk/clients/personalize";
import { EventNames } from "../../utils/EventTrackerConstants";
import RollbarErrorTracking from "../../utils/RollbarErrorTracking";
import IRoomEventLog, { IRoomEventOptionalLog } from "../../models/IRoomEventLog";
import BrowserUtils from "../../utils/BrowserUtils";
import { Tracker } from "../../utils/Tracker";



interface VideoCallContainerProps {
    videoCallRoomName: string;
    userIdentity: string;
    communicationAccessToken: string;
    onLeaveRoom: () => void;
    completeConsultationButtonClicked: () => void;
    isExpanded?: boolean;
    ongoingConsultation: IAppointmentScheduleResponse;
    updateOrderEvent: (orderEvent: IOrderEvent) => Promise<any>;
    addRoomEventLog: (reqBody: any, headers?: any) => Promise<any>;
    updateConnectionStrengthMessage: (connectionStrengthMessage: string) => void;
    fetchLogLevelForAgent(resourceId: number): Promise<IFetchConfigResponse>;
    doctorProfile: IDoctorProfile;
}


interface VideoCallContainerState {
    isMute: boolean
    isVideoOff: boolean;
    localVideoTrack: any;
    isPatientDisconnected: boolean;
    errorMessage: string;
    errorDetails?: string;
    activeRoom: Room;
    localAudioTrack: LocalAudioTrack;
    lastNetworkErrorTimeStamp: number;
    shoulShowActions: boolean;
}

declare var Twilio: any;

let identity: any;
let pollRoomStatsReference: any;

class VideoCallContainer extends React.Component<VideoCallContainerProps, VideoCallContainerState> {

    private agentLogLevel: IFetchConfigResponse = { configValue: null, configName: null };
    private logIntervalId: NodeJS.Timeout;
    private clearNetworkMessageTimeoutId: NodeJS.Timeout;
    private trackBeingPublish: string = null;
    private remoteParticipantIdentity: string = null
    private roomEventLogHeaders: any = {}

    constructor(props: VideoCallContainerProps) {
        super(props);
        this.state = {
            isMute: false,
            isVideoOff: false,
            localVideoTrack: undefined,
            isPatientDisconnected: true,
            errorMessage: null,
            activeRoom: null,
            localAudioTrack: null,
            lastNetworkErrorTimeStamp: null,
            shoulShowActions: true
        };
        this.disconnectCall = this.disconnectCall.bind(this);
        this.connectCall = this.connectCall.bind(this);
        this.roomEventLogHeaders = this.getLogEventHeaders()
    }

    getLogEventHeaders = () => {
        return {
            roomName: this.props.videoCallRoomName,
            memberType: "DOCTOR",
            platform: `${BrowserUtils.getBrowserName()}-${AppUtils.getIsMobile() ? "mweb" : "web"}`,
            version: navigator.appVersion
        }
    }

    logRoomEvent = (message: string, logs?: IRoomEventOptionalLog, shouldLogToCLoudWatch?: boolean) => {
        try {
            const reqBody: IRoomEventLog[] = []
            const roomEventLog: IRoomEventLog = ModelFactory.createNewAddRoomEventLogObject(
                new Date().getTime(),
                this.props.userIdentity,
                this.remoteParticipantIdentity,
                message
            );
            reqBody.push({ ...roomEventLog, ...logs })
            // reqBody.debugMessage = JSON.stringify({ message, NetworkInfo: AppUtils.getNetInfo(), browser: navigator.appVersion });
            if (!shouldLogToCLoudWatch) {
                this.props.addRoomEventLog(reqBody, this.roomEventLogHeaders);
            } else {

            }
        } catch (e) {
            console.log("Error logging room event")
        }
    }

    getOrderEvent = (status: string): IOrderEvent => {
        let self = this;
        let orderEvent: IOrderEvent = {
            bookingId: self.props.ongoingConsultation.bookingId.toString(),
            orderId: self.props.ongoingConsultation.id.toString(),
            eventType: status,
            metadata: { "roomName": self.props.videoCallRoomName }
        }
        return orderEvent
    }

    attachLocalParticipantTracks = (participant: LocalParticipant, container: HTMLElement, self: any) => {
        let publications = Array.from(participant.tracks.values());
        container.innerHTML = "";
        if (participant.identity === identity) {
            publications.forEach(function (publicaiton: LocalTrackPublication) {
                let track = publicaiton.track;
                if (track.kind === "audio") {
                    self.setState({
                        localAudioTrack: track,
                    });
                    if (self.state.isMute) {
                        self.muteLocalAudio();
                    }
                } else if (track.kind === "video") {
                    self.props.updateOrderEvent(self.getOrderEvent(JOIN_UNJOIN_CALL_EVENTS.DOCTOR_JOINED_CALL))
                    self.setState({
                        localVideoTrack: track,
                    });
                    if (self.state.isVideoOff) {
                        self.offLocalVideo();
                    }
                }
                container.appendChild((track as any).attach());
            });
        }
    }

    attachRemoteParticipantTracks = (participant: any, container: any, self: any) => {
        container.innerHTML = "";
        participant.tracks.forEach((publication: any) => {
            if (publication.isSubscribed) {
                const track = publication.track;
                container.appendChild(track.attach());
            }
        });
        participant.on("trackSubscribed", (track: any) => {
            container.appendChild(track.attach());
        });
    }


    bootstrapTwilioVideo = (userIdentity: string, accessToken: string, roomName: string) => {

        // When we are about to transition away from this page, disconnect
        // from the room, if joined.
        // window.addEventListener("beforeunload", this.leaveRoomIfJoined);

        identity = userIdentity;
        this.initiateVideoCall(roomName, accessToken);
    }

    pollRoomStats = () => {
        this.getRoomStats();
        pollRoomStatsReference = setTimeout(() => {
            this.pollRoomStats();
        }, 10000);
    }

    getRoomStats = () => {
        // console.log("TRACE:0 " , this.state.activeRoom);
        let maxPacketLossPercent = 0;
        let localPacketLost = 0;
        let remotePacketLost = 0;
        let localAudioPacketLost = 0;
        let localVideoPacketLost = 0;
        let remoteAudioPacketLost = 0;
        let remoteVideoPacketLost = 0;


        if (this.state.activeRoom) {
            this.state.activeRoom.getStats().then((roomStatsArray: StatsReport[]) => {
                // console.log("TRACE:1 " , JSON.stringify(roomStatsArray));
                roomStatsArray.forEach((roomStats: StatsReport) => {

                    roomStats.localAudioTrackStats.forEach((localAudioTrackStats: any) => {
                        if (localAudioTrackStats.packetsSent != 0) {
                            localAudioPacketLost = (localAudioTrackStats.packetsLost * 100) / localAudioTrackStats.packetsSent;
                            maxPacketLossPercent = Math.max(maxPacketLossPercent, localAudioPacketLost);
                            localPacketLost = Math.max(localPacketLost, localAudioPacketLost);
                            console.log("local audio packet lost : ", localAudioPacketLost);
                        }
                    });

                    roomStats.localVideoTrackStats.forEach((localVideoTrackStats: any) => {
                        if (localVideoTrackStats.packetsSent != 0) {
                            localVideoPacketLost = (localVideoTrackStats.packetsLost * 100) / localVideoTrackStats.packetsSent;
                            maxPacketLossPercent = Math.max(maxPacketLossPercent, localVideoPacketLost);
                            localPacketLost = Math.max(localPacketLost, localVideoPacketLost);
                            console.log("local video packet lost : ", localVideoPacketLost);
                        }
                    });

                    roomStats.remoteAudioTrackStats.forEach((remoteAudioTrackStats: any) => {
                        if (remoteAudioTrackStats.packetsSent !== 0) {
                            remoteAudioPacketLost = (remoteAudioTrackStats.packetsLost * 100) / remoteAudioTrackStats.packetsReceived;
                            maxPacketLossPercent = Math.max(maxPacketLossPercent, remoteAudioPacketLost);
                            remotePacketLost = Math.max(remotePacketLost, remoteAudioPacketLost);
                            console.log("remote audio packet lost : ", remoteAudioPacketLost);
                        }
                    });

                    roomStats.remoteVideoTrackStats.forEach((remoteVideoTrackStats: any) => {
                        if (remoteVideoTrackStats.packetsSent != 0) {
                            remoteVideoPacketLost = (remoteVideoTrackStats.packetsLost * 100) / remoteVideoTrackStats.packetsReceived;
                            maxPacketLossPercent = Math.max(maxPacketLossPercent, remoteVideoPacketLost);
                            remotePacketLost = Math.max(remotePacketLost, remoteVideoPacketLost);
                            console.log("remote video packet lost : ", remoteVideoPacketLost);
                        }
                    });
                });


                if (maxPacketLossPercent >= 5) {
                    let message = TwilioRoomEventsLog.PACKET_LOSS_IN_ORION + " : ";
                    // message += TwilioRoomEventsLog.LOCAL_AUDIO_PACKET_LOSS_IN_TWILIO + " : " + localAudioPacketLost;
                    // message += "," + TwilioRoomEventsLog.LOCAL_VIDEO_PACKET_LOSS_IN_TWILIO + " : " + localVideoPacketLost;
                    // message += "," + TwilioRoomEventsLog.REMOTE_AUDIO_PACKET_LOSS_IN_TWILIO + " : " + remoteAudioPacketLost;
                    // message += "," + TwilioRoomEventsLog.REMOTE_VIDEO_PACKET_LOSS_IN_TWILIO + " : " + remoteVideoPacketLost;
                    const optionalLogs: IRoomEventOptionalLog = {
                        packetLoss: true
                    }
                    console.log("Slow internet detected : ", message);
                    this.logRoomEvent(message, optionalLogs);

                    if (localPacketLost >= remotePacketLost) {
                        this.setState({
                            errorMessage: "Slow internet connection detected."
                        });
                    }
                    else {
                        this.setState({
                            errorMessage: "Slow internet connection detected on patient's side."
                        });
                    }
                } else {
                    this.setState({
                        errorMessage: null
                    });
                }

                // console.log("Local packet lost : " , localPacketLost);
                // console.log("Remote packet lost : ", remotePacketLost);
                // console.log("Max packet lost : " , maxPacketLossPercent);
            });
        }
    }

    logAvailableDevices = (self: any, room: any) => {
        AppUtils.getMediaDevices()
            .then((devices: string) => {
                const optionalLogs: IRoomEventOptionalLog = {
                    audioDevice: devices
                }
                self.logRoomEvent("ORION Available Devices", optionalLogs);
            })
            .catch(err => {
                const optionalLogs: IRoomEventOptionalLog = {
                    error: err.toString()
                }
                self.logRoomEvent("ORION Available Devices Error", optionalLogs);
            })
    }

    isPoorQualityNetwork = (networkQualityLevel: NetworkQualityLevel) => {
        return networkQualityLevel <= AppConstants.SLOW_NETWORK_THRESHOLD;
    };

    initiateVideoCall = (roomName: string, accessToken: string) => {
        let self = this;

        if (roomName) {
            self.log("Joining room '" + roomName + "'...");

            twilioVideoConnect(accessToken, {
                name: roomName,
                logLevel: "off",
                audio: { name: "microphone" },
                video: { name: "camera", },
                region: "in1" as any,
                enableDscp: true,
                networkQuality: {
                    local: 3,
                    remote: 3
                }
            }).then((room: Room) => {
                document.dispatchEvent(MobileConstants.CALL_CONNECTED);
                try {
                    self.props.updateOrderEvent(self.getOrderEvent(JOIN_UNJOIN_CALL_EVENTS.DOCTOR_JOINED_CALL))
                } catch (e) {
                    console.log("Error logging order event")
                }
                console.log(`Connected to room : ${room.name}`);
                this.logAvailableDevices(self, room);
                const localParticipant = room.localParticipant;

                navigator.mediaDevices.ondevicechange = (event: Event) => {
                    navigator.mediaDevices.getUserMedia({ audio: true, video: true })  // in future handle video input change as well
                        .then((stream: MediaStream) => {

                            const presentLocalMediaTracks: LocalAudioTrack[] = [];
                            localParticipant.audioTracks.forEach((audioTrack: LocalAudioTrackPublication) => {
                                presentLocalMediaTracks.push(audioTrack.track);
                            });

                            localParticipant.unpublishTracks(presentLocalMediaTracks);

                            const uniqueCapturedAudioTracks: MediaStreamTrack[] = _.uniqBy(stream.getTracks().filter(({ kind }) => kind === "audio"), "label");
                            console.log("Unique tracks", uniqueCapturedAudioTracks);
                            if (this.trackBeingPublish !== uniqueCapturedAudioTracks[0].label) {
                                this.trackBeingPublish = uniqueCapturedAudioTracks[0].label;

                                localParticipant.publishTracks(uniqueCapturedAudioTracks)
                                    .then((mediaTracks: LocalTrackPublication[]) => {
                                        this.attachLocalParticipantTracks(room.localParticipant, previewContainer, this);
                                        this.trackBeingPublish = null;
                                    }).catch(msg => {
                                        const optionalLogs: IRoomEventOptionalLog = {
                                            error: JSON.stringify(msg)
                                        }
                                        self.logRoomEvent("LOCAL_TRACK_DEVICE_CHANGE_ERROR", optionalLogs);
                                        this.trackBeingPublish = null;
                                    });
                            }
                        }).catch((error: any) => {
                            let debugMessage, errorMsg = error.name
                            if (error.name === "ConstraintNotSatisfiedError") {
                                debugMessage = "The resolution is not supported by your device."
                            } else if (error.name === "PermissionDeniedError") {
                                debugMessage = "Permissions have not been granted to use your camera and microphone"
                            }
                            const optionalLogs: IRoomEventOptionalLog = {
                                error: errorMsg
                            }
                            self.logRoomEvent(debugMessage, optionalLogs);
                        })

                }


                self.setState({
                    activeRoom: room
                });

                // Log your Client's LocalParticipant in the Room
                console.log("Connected to the Room as LocalParticipant ${localParticipant.identity}");

                room.localParticipant.on("networkQualityLevelChanged", (networkQualityLevel: number, networkQualityStats: NetworkQualityStats) => {
                    const isPoorQualityNetwork = this.isPoorQualityNetwork(room.localParticipant.networkQualityLevel)
                    const debugMessage = TwilioRoomEventsLog.NETWORK_QUALITY_CHANGED
                    let optionalLogs: IRoomEventOptionalLog = {
                        localNetworkQualityLevel: networkQualityLevel,
                        poorNetwork: isPoorQualityNetwork
                    }

                    if (isPoorQualityNetwork &&
                        (new Date().getTime() - this.state.lastNetworkErrorTimeStamp >= 2 * EpochConstants.EPOCH_IN_A_MIN)
                    ) {
                        if (this.clearNetworkMessageTimeoutId) {
                            clearInterval(this.clearNetworkMessageTimeoutId);
                        }
                        this.setState({ errorMessage: AppConstants.SLOW_NETWORK_MESSAGE });
                        optionalLogs["showNetworkQualityBad"] = true
                        setTimeout(() => {
                            clearInterval(this.clearNetworkMessageTimeoutId);
                            if (this.state.errorMessage === AppConstants.SLOW_NETWORK_MESSAGE) {
                                this.setState({ errorMessage: null });
                            }
                        }, AppConstants.SLOW_NETWORK_MESSAGE_CLEAR_INTERVAL_MS);
                    }
                    this.logRoomEvent(debugMessage, optionalLogs)

                });

                // Add self preview
                let previewContainer = document.getElementById("local-media");
                if (!previewContainer.querySelector("video")) {
                    console.log("Adding local participant in docsapp");
                    const optionalLogs: IRoomEventOptionalLog = {
                        localMemberConnection: "connected"
                    }
                    self.logRoomEvent(TwilioRoomEventsLog.DOCTOR_JOINED_IN_DOCSAPP, optionalLogs);
                    self.attachLocalParticipantTracks(localParticipant, previewContainer, self);
                }


                // Log any Participants already connected to the Room
                room.participants.forEach((participant: RemoteParticipant) => {
                    console.log(`Participant "${participant.identity}" is already connected to the Room`);
                    this.remoteParticipantIdentity = participant.identity
                    let remotePreviewContainer = document.getElementById("remote-media");
                    const optionalLogs: IRoomEventOptionalLog = {
                        remoteMemberConnection: "connected"
                    }
                    self.logRoomEvent(TwilioRoomEventsLog.PATIENT_ALREADY_JOINED_IN_DOCSAPP, optionalLogs);
                    self.attachRemoteParticipantTracks(participant, remotePreviewContainer, self);
                    self.setState({
                        isPatientDisconnected: false
                    });
                    participant.on("networkQualityLevelChanged", (networkQualityLevel: number, networkQualityStats: NetworkQualityStats) => {
                        console.log("Network Quality Level", networkQualityLevel, networkQualityLevel);
                        const isPoorNetwork: boolean = this.isPoorQualityNetwork(networkQualityLevel)
                        const optionalLogs: IRoomEventOptionalLog = {
                            remoteNetworkQualityLevel: networkQualityLevel,
                            poorNetwork: isPoorNetwork
                        }
                        self.logRoomEvent(TwilioRoomEventsLog.NETWORK_QUALITY_CHANGED, optionalLogs);
                    });
                });

                // Log new Participants as they connect to the Room
                room.on("participantConnected", (participant: any) => {
                    console.log(`Participant "${participant.identity}" has connected to the Room`);
                    this.remoteParticipantIdentity = participant.identity
                    let remotePreviewContainer = document.getElementById("remote-media");
                    self.attachRemoteParticipantTracks(participant, remotePreviewContainer, self);
                    const optionalLogs: IRoomEventOptionalLog = {
                        remoteMemberConnection: "connected"
                    }
                    self.logRoomEvent(TwilioRoomEventsLog.PATIENT_JOINED_IN_DOCSAPP, optionalLogs);
                    self.setState({
                        isPatientDisconnected: false
                    });
                });

                // Log Participants as they disconnect from the Room
                room.on("participantDisconnected", (participant: any) => {
                    console.log(`Participant "${participant.identity}" has disconnected from the Room`);
                    const optionalLogs: IRoomEventOptionalLog = {
                        remoteMemberConnection: "disconnected"
                    }
                    self.logRoomEvent(TwilioRoomEventsLog.PATIENT_DISCONNECTED_IN_DOCSAPP, optionalLogs);
                    let remotePreviewContainer = document.getElementById("remote-media");
                    remotePreviewContainer.innerHTML = "";
                    self.setState({
                        isPatientDisconnected: true
                    });
                });

                room.on("disconnected", (room: any) => {
                    // Detach the local media elements
                    document.dispatchEvent(MobileConstants.CALL_DISCONNECTED);
                    room.localParticipant.tracks.forEach((publication: any) => {
                        const attachedElements = publication.track.detach();
                        attachedElements.forEach((element: any) => element.remove());
                    });
                    previewContainer.innerHTML = "";
                    const optionalLogs: IRoomEventOptionalLog = {
                        localMemberConnection: "disconnected-manual"
                    }
                    self.logRoomEvent(TwilioRoomEventsLog.DOCTOR_DISCONNECTED_IN_DOCSAPP, optionalLogs);
                });

                room.on("reconnecting", (error: any) => {
                    const optionalLogs: IRoomEventOptionalLog = {
                        localMemberConnection: "reconnecting",
                        error: this.getRoomEventErrorDescription(error)
                    }
                    self.logRoomEvent(TwilioRoomEventsLog.DOCTOR_RECONNECTING_TO_TWILIO, optionalLogs);
                });

                room.on("participantReconnecting", remoteParticipant => {
                    const optionalLogs: IRoomEventOptionalLog = {
                        remoteMemberConnection: "reconnecting",
                    }
                    self.logRoomEvent(TwilioRoomEventsLog.PATIENT_RECONNECTING_TO_TWILIO, optionalLogs);
                });

                room.on("reconnected", () => {
                    console.warn("Twilio reconnected!");
                    const optionalLogs: IRoomEventOptionalLog = {
                        localMemberConnection: "connected"
                    }
                    self.logRoomEvent(TwilioRoomEventsLog.DOCTOR_RECONNECTING_TO_TWILIO, optionalLogs);
                    self.attachLocalParticipantTracks(localParticipant, previewContainer, self);
                });

                room.on("error", (error: any) => {
                    console.warn("Twilio reconnected!", error);
                    const optionalLogs: IRoomEventOptionalLog = {
                        localMemberConnection: "disconnected-auto",
                        error: this.getRoomEventErrorDescription(error)
                    }
                    self.logRoomEvent(TwilioRoomEventsLog.DOCTOR_RECONNECTING_TO_TWILIO, optionalLogs);
                    self.attachLocalParticipantTracks(localParticipant, previewContainer, self);
                });

            }).catch(async (error: any) => {
                console.log("Error in conencting to room");
                let additionalMsg: string[] = []
                try {
                    await navigator.mediaDevices.getUserMedia({ audio: true })
                } catch (e) {
                    additionalMsg.push("Microphone permission not found")
                }
                try {
                    await navigator.mediaDevices.getUserMedia({ video: true })
                } catch (e) {
                    additionalMsg.push("Video permission not found")
                }
                this.setState({
                    errorMessage: "Could not enable video.",
                    errorDetails: error.message.toString()
                }, () => {
                    self.logRoomEvent("ORION_Room" + error.message.toString() + " " + additionalMsg.join(","));
                })
            });
        } else {
            alert("Please enter a room name.");
        }
    }

    getRoomEventErrorDescription = (error: any) => {
        return `(${error.code}) - ${error.message}`
    }

    // Activity log
    log = (message: any) => {
        let logDiv = document.getElementById("log");
        logDiv.innerHTML += "<p>&gt;&nbsp;" + message + "</p>";
        logDiv.scrollTop = logDiv.scrollHeight;
    }

    leaveRoomIfJoined = (disableLogOrderEvent?: boolean) => {
        if (this.state.activeRoom) {
            if (!disableLogOrderEvent) {
                this.props.updateOrderEvent(this.getOrderEvent(JOIN_UNJOIN_CALL_EVENTS.DOCTOR_UNJOINED_CALL))
            }
            const optionalLogs: IRoomEventOptionalLog = {
                localMemberConnection: "disconnected-manual"
            }
            this.logRoomEvent(TwilioRoomEventsLog.DOCTOR_LEAVING_ROOM_IN_DOCSAPP, optionalLogs);
            this.state.activeRoom.disconnect();
        }
    }

    muteLocalAudio = () => {
        if (this.state.localAudioTrack) {
            const optionalLogs: IRoomEventOptionalLog = {
                audio: false
            }
            this.state.localAudioTrack.disable();
            this.logRoomEvent(TwilioRoomEventsLog.MUTED, optionalLogs);
        }
    }
    unmuteLocalAudio = () => {
        if (this.state.localAudioTrack) {
            const optionalLogs: IRoomEventOptionalLog = {
                audio: true
            }
            this.state.localAudioTrack.enable();
            this.logRoomEvent(TwilioRoomEventsLog.UNMUTED, optionalLogs);
        }
    }

    offLocalVideo = () => {
        if (this.state.localVideoTrack) {
            const optionalLogs: IRoomEventOptionalLog = {
                video: false
            }
            this.state.localVideoTrack.disable();
            this.logRoomEvent(TwilioRoomEventsLog.VIDEO_DISABLED, optionalLogs);
        }
    }

    onLocalVideo = () => {
        if (this.state.localVideoTrack) {
            const optionalLogs: IRoomEventOptionalLog = {
                audio: true
            }
            this.state.localVideoTrack.enable();
            this.logRoomEvent(TwilioRoomEventsLog.VIDEO_ENABLED, optionalLogs);
        }
    }


    phoneButtonClicked = (event: any) => {

    }

    muteToggleButtonClicked = (event: any) => {
        if (this.state.isMute) {
            LocalStorageUtils.setClickEvents({ field: "Unmute Audio", section: EventNames.Call_Widget })
            this.unmuteLocalAudio();
        } else {
            LocalStorageUtils.setClickEvents({ field: "Mute Audio", section: EventNames.Call_Widget })
            this.muteLocalAudio();
        }
        this.setState({
            isMute: !this.state.isMute
        });
    }

    videoToggleButtonClicked = (event: any) => {
        if (this.state.isVideoOff) {
            LocalStorageUtils.setClickEvents({ field: "Unmute Video", section: EventNames.Call_Widget, widget: "Video Call" })
            this.onLocalVideo();
        } else {
            LocalStorageUtils.setClickEvents({ field: "Mute Video", section: EventNames.Call_Widget, widget: "Video Call" })
            this.offLocalVideo();
        }
        this.setState({
            isVideoOff: !this.state.isVideoOff
        });
    }

    hangupButtonClicked = (event: any) => {
        this.leaveRoomIfJoined();
        this.props.onLeaveRoom();
    }

    completeConsultationButtonClicked = (event: any) => {
        let value = confirm("Are you sure you want to Disconnect ? Once disconnected , you can't connect back");
        if (value) {
            this.leaveRoomIfJoined();
            this.props.completeConsultationButtonClicked();
        }
    }

    componentWillUnmount() {
        if (this.state.localVideoTrack) this.state.localVideoTrack.stop();
        clearTimeout(pollRoomStatsReference);
        this.props.updateOrderEvent(this.getOrderEvent(JOIN_UNJOIN_CALL_EVENTS.DOCTOR_UNJOINED_CALL))
        this.leaveRoomIfJoined(true);
        window.onmessage = null;
        if (this.logIntervalId) {
            clearInterval(this.logIntervalId);
        }
    }


    componentDidMount() {
        if (isSupported) {
            this.connectCall();
        } else {
            AppUtils.triggerToastNotification("This browser is not supported by twilio-video.js", toastNotificationAppearance.error);
            /*
            RollbarErrorTracking.logErrorInRollbar(new Error("Browser not supported by twilio-video"), {
                doctorId: _.get(this.props.doctorProfile, "id", null),
                videoCallRoomName: this.props.videoCallRoomName,
                userIdentity: this.props.userIdentity
            })
            */
        }
    }

    private connectCall(): void {
        /*        LocalStorageUtils.setClickEvents({ field: "Join Call", section: EventNames.Call_Widget,  widget: "Video Call" })*/
        this.props.fetchLogLevelForAgent(this.props.doctorProfile.id)
            .then(res => {
                console.log(res);
                this.agentLogLevel = res;
                if (this.agentLogLevel.configValue) {
                    this.logIntervalId = setInterval(() => {
                        const optionalLogs: IRoomEventOptionalLog = {
                            audio: !this.state.isMute,
                            video: !this.state.isVideoOff,
                            remoteMemberConnection: this.state.isPatientDisconnected ? "disconnected" : "connected"
                        }
                        this.logRoomEvent(TwilioRoomEventsLog.VIDEO_CALL_METRICS_INFO, optionalLogs);
                    }, AppConstants.LOG_INTERVAL_IN_MS);
                }
            })
            .catch(err => {
                console.log(err);
            })

        console.log("SUMIT:  " + this.props.communicationAccessToken);
        this.pollRoomStats();
        this.bootstrapTwilioVideo(this.props.userIdentity, this.props.communicationAccessToken, this.props.videoCallRoomName);
    }
    private disconnectCall(): void {
        LocalStorageUtils.setClickEvents({ field: "End Call", section: EventNames.Call_Widget, widget: "Video Call" });
        this.state.activeRoom.localParticipant.tracks.forEach((localTrackPublication: LocalTrackPublication) => localTrackPublication.track.stop());
        this.state.activeRoom.localParticipant.tracks.forEach((localTrackPublication: LocalTrackPublication) => localTrackPublication.unpublish());
        this.state.activeRoom.disconnect();
    }

    disableErrorMessage = () => {
        this.setState({
            errorMessage: null
        })
    }

    disableErrorDetails = () => {
        this.setState({
            errorDetails: null
        })
    }

    render() {
        const isMobile = AppUtils.getIsMobile();
        const isDisconnected = this.state.activeRoom && this.state.activeRoom.localParticipant && this.state.activeRoom.localParticipant.state !== "connected";
        let localMediaSectionClasses = "healthface-local-video-container " + (this.props.isExpanded ? "video-local-media-expanded-size" : "video-local-media-collapsed-size");
        let errorLabelStyles = { padding: "4px 5px 2px 5px", color: "white", display: "inline-block", background: "#e65f79", borderRadius: "4px", opacity: 0.5, fontSize: "12px" };
        return (
            <div className="dashboard-left-view-wrapper" onMouseEnter={() => this.setState({ shoulShowActions: true })}
                onMouseLeave={() => this.setState({ shoulShowActions: false })}>
                {
                    !_.isNull(this.state.errorMessage) &&
                    <div className={"floating-message animated flipInX"}>
                        <div>
                            <span>{this.state.errorMessage}</span>
                            <span className={"disable-error"} onClick={this.disableErrorMessage}>x</span>
                        </div>
                        {
                            this.state.errorDetails &&
                            <div>
                                <span>{this.state.errorDetails}</span>
                                <span className={"disable-error"} onClick={this.disableErrorDetails}>x</span>
                            </div>
                        }
                    </div>
                }
                <div id="remote-media" className="healthface-remote-video-container"
                    style={{ width: "100%", height: "100%", overflow: "hidden", transform: "scale(-1, 1)" }}></div>
                {
                    this.state.isPatientDisconnected &&
                    <div id="patient-disconnected" className="healthface-remote-video-container"
                        style={{ width: "100%", height: "100%", color: "red", overflow: "hidden", position: "absolute", top: "0" }}>
                        <span className={"error-label"} style={errorLabelStyles}>Patient has not joined</span>
                    </div>
                }
                <div id="controls" style={{ position: "absolute", right: "0px", bottom: "0px", height: "40%" }}>
                    <div id="preview" style={{ height: "100%" }}>
                        <div id="local-media" className={localMediaSectionClasses}></div>
                        {/*<button id="button-preview">Preview My Camera</button>*/}
                    </div>
                    <div id="log" style={{ display: "None" }}></div>
                </div>
                <div style={{ position: "absolute", width: "100%", bottom: "5px" }}>
                    {(this.state.shoulShowActions || isDisconnected || isMobile) && this.state.localVideoTrack !== undefined && this.state.localVideoTrack !== null &&    // Commenting this  - Sumit ( 7th Dec 2017)
                        <VideoCallActionBar
                            isMute={this.state.isMute}
                            isVideoOff={this.state.isVideoOff}
                            phoneButtonClicked={this.phoneButtonClicked}
                            muteToggleButtonClicked={this.muteToggleButtonClicked}
                            videoToggleButtonClicked={this.videoToggleButtonClicked}
                            hangupButtonClicked={this.hangupButtonClicked}
                            completeConsultationButtonClicked={this.completeConsultationButtonClicked}
                            disconnectCall={this.disconnectCall}
                            isDisconnected={isDisconnected}
                            connectCall={this.connectCall} />

                    }
                </div>
            </div>
        );
    }
}


function mapStateToProps(state: any, ownProps: any): any {
    return {
        ongoingConsultation: state.ongoingConsultation.appointmentSchedule,
        doctorProfile: state.doctorProfile
    };
}

function mapDispatchToProps(dispatch: any): any {
    return {
        updateOrderEvent: (orderEvent: IOrderEvent) => {
            return OrderActions.logOrderEvent(dispatch, orderEvent)
        },
        addRoomEventLog: (reqBody: any, headers?: any) => {
            return OrderActions.logRoomEvent(dispatch, reqBody, headers);
        },
        fetchLogLevelForAgent: (doctorId: number) => OngoingConsultationActions.fetchLogLevelForAgent(doctorId)
        // updateConnectionStrengthMessage: (connectionStrengthMessage: string) => {
        //     return TwilioActions.updateConnectionStrength(dispatch, connectionStrengthMessage);
        // }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(VideoCallContainer);